<template>
  <v-dialog
    lazy
    :value="show"
    persistent
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :width="$store.state.common.largeDialogWidth"
    @input="(val) => $emit('update:show', val)"
  >
    <v-card>
      <v-card-title class="pb-0">
        <v-layout
          row
          align-center
          justify-center
        >
          <v-icon
            medium
            class="msr--text mr-2"
          >
            date_range
          </v-icon>
          <h1 class="headline">
            {{ $t('contentHardRangeSet') }}
          </h1>
          <v-btn
            class="ml-auto mr-0 my-0"
            :disabled="loading"
            flat
            icon
            :title="$t('close')"
            @click="close()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <div class="subheading">
          {{ $t('contentHardRangeSetHint') }}
          <v-layout
            v-if="showEntriesWarning"
            class="mt-2"
            justify-center
            align-center
            :column="$vuetify.breakpoint.xsOnly"
          >
            <v-icon
              color="warning"
              class="mr-3 mb-2"
            >
              warning
            </v-icon>
            <span class="warning--text">{{
              $t('contentHardRangeAffectEntriesWarning')
            }}</span>
          </v-layout>
        </div>
        <!-- Hard Range Controls-->
        <v-layout
          class="mt-3"
          :wrap="$vuetify.breakpoint.mdAndDown"
        >
          <!-- Hard Start -->
          <div class="menu-container">
            <v-menu
              v-model="hardStartMenu"
              lazy
              offset-y
              :class="{'mr-4': !newHardStart && $vuetify.breakpoint.smAndUp}"
              :min-width="mobileDatepickerMinWidth"
              :nudge-left="40"
            >
              <v-text-field
                slot="activator"
                prepend-icon="event"
                readonly
                data-cy="new-hard-start-textfield"
                :placeholder="newHardStartPlaceholder"
                :value="newHardStart"
                :label="$t('hardStart')"
              />
              <v-date-picker
                v-model="newHardStart"
                color="msr"
                scrollable
                event-color="msr"
                :no-title="$vuetify.breakpoint.xsOnly"
                :first-day-of-week="1"
                :locale="$t('_datepickerLocale')"
                :allowed-dates="isNotInPast"
              />
            </v-menu>
            <v-btn
              v-if="newHardStart"
              flat
              icon
              class="ml-0"
              @click="newHardStart = null"
            >
              <v-icon class="msr--text">
                clear
              </v-icon>
            </v-btn>
          </div>
          <!-- Hard Expire -->
          <div class="menu-container">
            <v-menu
              v-model="hardExpireMenu"
              lazy
              offset-y
              :min-width="mobileDatepickerMinWidth"
              :nudge-left="40"
            >
              <v-text-field
                slot="activator"
                prepend-icon="event"
                readonly
                data-cy="new-hard-expire-textfield"
                :placeholder="newHardExpirePlaceholder"
                :value="newHardExpire"
                :label="$t('hardExpire')"
              />
              <v-date-picker
                v-model="newHardExpire"
                color="msr"
                scrollable
                event-color="msr"
                :no-title="$vuetify.breakpoint.xsOnly"
                :first-day-of-week="1"
                :locale="$t('_datepickerLocale')"
                :allowed-dates="isNotInPast"
              />
            </v-menu>
            <v-btn
              v-if="newHardExpire"
              flat
              icon
              class="ml-0"
              @click="newHardExpire = null"
            >
              <v-icon class="msr--text">
                clear
              </v-icon>
            </v-btn>
          </div>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <btn-ok
          round
          data-cy="confirm-hard-range-dialog-btn"
          :loading="loading"
          @click="confirm()"
        />
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import mobileDatepickerMinWidth from '@/mixins/mobileDatepickerMinWidth.js';
import timing from '@/mixins/timing.js';

import BtnOk from '@/components/ButtonOk';

export default {
  components: {
    BtnOk,
  },
  mixins: [mobileDatepickerMinWidth, timing],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    hardStart: {
      type: [Date, String],
      default: null,
    },
    hardExpire: {
      type: [Date, String],
      default: null,
    },
    /**
     * Control whether we automatically subtract one day from the
     * initial hardExpire here before displaying it in the UI.
     *
     * Whether this is necessary or not depends on where the value comes
     * from. In general, the user-chosen date will be saved to the db as
     * +1 day, so the chosen day is fully included. Before displaying
     * that saved value in the UI, we therefore need to -1 day.
     */
    hardExpireOffset: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showEntriesWarning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hardExpireMenu: false,
      hardStartMenu: false,

      newHardStart: null,
      newHardExpire: null,
    };
  },
  computed: {
    newHardStartPlaceholder() {
      return this.newHardStart ? '' : this.$t('toUnlimited');
    },
    newHardExpirePlaceholder() {
      return this.newHardExpire ? '' : this.$t('toUnlimited');
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        this.initializeNewHardRangeValues(this.hardStart, this.hardExpire);
      }
    },
    newHardStart: function (newHardStart) {
      if (newHardStart && this.newHardExpire) {
        if (this._isBefore(this.newHardExpire, newHardStart)) {
          // Chosen new hard_start invalidates the current hard_expire.
          this.newHardExpire = null;
        }
      }
    },
    newHardExpire: function (newHardExpire) {
      if (this.newHardStart && newHardExpire) {
        if (this._isBefore(newHardExpire, this.newHardStart)) {
          // Chosen new hard_expire invalidates the current hard_start.
          this.newHardStart = null;
        }
      }
    },
  },
  methods: {
    initializeNewHardRangeValues(oldHardStart, oldHardExpire) {
      this.newHardStart = oldHardStart ? this.formatDate(oldHardStart) : null;
      this.newHardExpire = oldHardExpire
        ? this.formatDate(
            this.hardExpireOffset
              ? this.subtractOneDayFromDate(oldHardExpire)
              : oldHardExpire
          )
        : null;
    },
    confirm() {
      this.$emit('confirm', {
        newHardStart: this.newHardStart,
        newHardExpire: this.newHardExpire,
      });
      this.close();
    },
    close() {
      this.$emit('update:show', false);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/EditHardRangeDialog.vue